import { CardContent } from "@/components/ui/card";
import { useState, useEffect, useRef } from "react";
import { ProfileAvatar } from "./header/ProfileAvatar";
import { ProfileInfo } from "./header/ProfileInfo";
import { ProfileSkillsEditor } from "./header/ProfileSkillsEditor";
import { ProfileActions } from "./header/ProfileActions";
import { ProfileStats } from "./header/ProfileStats";
import { toast } from "@/components/ui/use-toast";
import { useFreelancerStore } from "@/store/freelancerStore";
import imagePlaceholder from "@/assets/images/image.png";

interface ProfileHeaderProps {
  isEditing: boolean;
  isFromAIRecommFreelancerView?: boolean;
  formData: {
    fullName: string;
    designation: string;
    reviews?: object;
    profilePicture: string;
    totalEarnings: number;
    projectsCompleted: number;
    location: string;
    availability: "available" | "offline";
    skills: string[];
    category: string;
    experience: string;
    dailyRate: number;
    phone: string;
    avatarUrl?: string;
    _id: string;
  };
  handleInputChange?: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  setIsEditing?: (value: boolean) => void;
  setFormData?: (data: any) => void;
  updateFreelancerProfile?: (
    id: string,
    data: Partial<any>,
    token: string
  ) => Promise<void>;
  uploadFreelancerProfilePicture?: (
    file: File,
    token: string,
    userId: string,
    uploadType?: string
  ) => Promise<any>;
  accessToken?: string | null;
  userId?: string;
  toast?: (options: Parameters<typeof toast>[0]) => void;
  freelancerId?: string | null;
}

const ProfileHeader = ({
  isEditing,
  isFromAIRecommFreelancerView,
  formData,
  handleInputChange,
  setIsEditing,
  setFormData,
  updateFreelancerProfile,
  uploadFreelancerProfilePicture,
  accessToken,
  userId,
  toast,
  freelancerId,
}: ProfileHeaderProps) => {
  const { profile: profileData } = useFreelancerStore();
  
  const [newSkill, setNewSkill] = useState("");
  const [localSkills, setLocalSkills] = useState<string[]>([]);
  const [localAvailability, setLocalAvailability] = useState<
    "available" | "offline"
  >(formData.availability);
  const [avatarPreview, setAvatarPreview] = useState<string>(
    formData.avatarUrl || imagePlaceholder
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync local state with formData
  useEffect(() => {
    if (formData.skills) {
      setLocalSkills(formData.skills);
    }
    if (formData.availability) {
      setLocalAvailability(formData.availability);
    }
    if (formData.profilePicture) {
      setAvatarPreview(formData.profilePicture);
    }
  }, [formData]);

  const handleSettingChange = (key: string, value: any) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleAvatarClick = () => {
    if (isEditing && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleAvatarChange = async (previewUrl: string, file: File) => {
    // Revoke old preview URL
    if (avatarPreview && typeof avatarPreview === 'string') {
      URL.revokeObjectURL(avatarPreview);
    }
    setAvatarPreview(previewUrl);

    // Set the new avatar preview URL in form data
    setFormData?.((prev: any) => ({
      ...prev,
      avatarUrl: previewUrl,
    }));

    // Upload the file
    await handleUploadProfilePicture(file);

    // Reset file input to allow re-uploading same file
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUploadProfilePicture = async (file: File) => {
    try {
      await uploadFreelancerProfilePicture(file, accessToken, userId);
      toast({
        title: "Profile Picture Updated",
        description: "Your enterprise profile picture has been successfully uploaded.",
        variant: "success",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to upload profile picture. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleAddSkill = async () => {
    if (newSkill.trim()) {
      const newSkills = newSkill
        .split(",")
        .map((skill) => skill.trim())
        .filter((skill) => skill.length > 0 && !localSkills.includes(skill));

      if (newSkills.length > 0) {
        const updatedSkills = [...localSkills, ...newSkills];
        await updateFreelancerProfile(
          formData._id,
          { skills: updatedSkills },
          accessToken
        );

        setNewSkill("");
      }
    }
  };

  const handleRemoveSkill = (skillToRemove: string) => {
    const updatedSkills = localSkills.filter(
      (skill) => skill !== skillToRemove
    );
    setLocalSkills(updatedSkills);
    setFormData((prev) => ({ ...prev, skills: updatedSkills }));
  };

  const handleSave = async () => {
    if (!accessToken || !userId) {
      toast({
        title: "Error",
        description: "You must be logged in to update your profile.",
        variant: "destructive",
      });
      return;
    }

    const updatedData = {
      fullName: formData.fullName,
      designation: formData.designation,
      category: formData.category,
      experience: formData.experience,
      dailyRate: Number(formData.dailyRate),
      phone: formData.phone,
      location: formData.location,
      skills: localSkills,
      availability: formData.availability === "available",
    };

    try {
      await updateFreelancerProfile(formData._id, updatedData, accessToken);
      setIsEditing(false);
      toast({
        title: "Profile Updated",
        description: "Your profile information has been successfully updated.",
        variant: "success",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    }
  };

  const checkFormValidity = () => {
    return formData.designation.trim() !== "";
  };

  const handleCancel = () => {
    setFormData({
      ...profileData,
      fullName: profileData?.fullName || "",
      designation: profileData?.designation || "",
      totalEarnings: profileData?.totalEarnings || 0,
      projectsCompleted: profileData?.projectsCompleted || 0,
      category: profileData?.category || "",
      experience: profileData?.experience || "",
      dailyRate: profileData?.dailyRate || 0,
      phone: profileData?.phone || "",
      reviews: profileData?.reviews || 0,
      location: profileData?.location || "",
      skills: profileData?.skills || [],
      availability: profileData?.availability ? "available" : "offline",
    });
  };

  return (
    <CardContent className={!isFromAIRecommFreelancerView ? "pt-6" : "p-0"}>
      <ProfileAvatar
        avatarPreview={avatarPreview}
        isEditing={isEditing}
        localAvailability={localAvailability}
        handleAvatarClick={handleAvatarClick}
        fileInputRef={fileInputRef}
        onAvatarChange={handleAvatarChange}
      />

      <ProfileInfo
        isEditing={isEditing}
        formData={formData}
        handleInputChange={handleInputChange}
        handleSettingChange={handleSettingChange}
      />

      <ProfileStats
        reviews={formData.reviews}
        projectsCompleted={formData.projectsCompleted}
        dailyRate={formData.dailyRate}
      />

      <ProfileSkillsEditor
        isEditing={isEditing}
        localSkills={localSkills}
        handleRemoveSkill={handleRemoveSkill}
        handleAddSkill={handleAddSkill}
        newSkill={newSkill}
        setNewSkill={setNewSkill}
      />
      {!isFromAIRecommFreelancerView && !freelancerId &&
        <ProfileActions
          isEditing={isEditing}
          checkFormValidity={checkFormValidity}
          handleSave={handleSave}
          setIsEditing={setIsEditing}
          handleCancel={handleCancel}
        />
      }
    </CardContent>
  );
};

export default ProfileHeader;
